"use client";

import { ApiPermissions } from "@cede/types";
import { CEDE_CEX_SIGN_UP_URLS } from "@cede/utils";
import { useTutorials } from "./useTutorials";
import { TutorialReader } from "@/components/features/onboarding/Tutorial/TutorialReader";
import { SkeletonElement } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

interface FullPageTutorialsProps {
  cexId: string;
  className?: string;
  isMobile?: boolean;
}

const defaultPermissions = [ApiPermissions.READ, ApiPermissions.TRADE];

export const FullPageTutorials = ({ cexId, className, isMobile = false }: FullPageTutorialsProps) => {
  const { cex, tutorialType, mdContent, isLoading } = useTutorials(cexId);
  const handleAffiliationClick = () => {
    const signUpUrl = CEDE_CEX_SIGN_UP_URLS[cexId];
    if (!signUpUrl) return;
    window.open(signUpUrl, "_blank", "noopener,noreferrer");
  };

  if (isLoading) {
    return (
      <div
        className={cn(
          "flex h-full min-h-0 min-w-0 flex-1 flex-col",
          !isMobile && "rounded-2xl border border-cede-border-container bg-cede-bg-component/60 p-6 shadow-sm",
          isMobile && "px-2",
          className,
        )}
      >
        <div className="mb-4">
          <div className="h-6 w-40 rounded bg-cede-bg-component/40" />
          <div className="mt-2 h-4 w-56 rounded bg-cede-bg-component/30" />
        </div>
        <div className="flex-1 space-y-4 overflow-hidden">
          <SkeletonElement className="h-[140px] w-full rounded-xl" />
          <SkeletonElement className="h-[140px] w-full rounded-xl" />
          <SkeletonElement className="h-[140px] w-full rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex h-full min-h-0 min-w-0 flex-1 flex-col",
        !isMobile && "rounded-2xl border border-cede-border-container bg-cede-bg-component/60 p-6 shadow-sm",
        isMobile && "px-2",
        className,
      )}
    >
      <div className="mb-4 flex flex-col gap-1">
        <h3 className="text-lg font-semibold text-cede-text-primary">
          {cex.label ? `How to connect ${cex.label}` : "How to connect your exchange"}
        </h3>
        <p className="text-sm text-cede-text-secondary">Follow these quick steps to generate and link your API keys.</p>
      </div>
      <div className={cn("flex-1 min-h-0 overflow-y-auto scrollbar-visible", !isMobile && "pr-2")}>
        <TutorialReader
          folder={tutorialType}
          mdContent={mdContent}
          permissions={defaultPermissions}
          accountPermissions={defaultPermissions}
          exchangeId={cex.value}
          actionProps={{
            affiliation: {
              onClick: handleAffiliationClick,
            },
          }}
        />
      </div>
    </div>
  );
};
